import { useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { Copy } from "lucide-react";
import { toast } from "react-toastify";
import { useSubMenu } from "../../../../../empreenderUI/SubMenuContext";
import ScrollArea from "../../../../../empreenderUI/components/ScrollArea";
import Heading from "../../../../../empreenderUI/ui/Heading/Heading";
import { Textarea } from "../../../../../empreenderUI/ui/Textarea";
import { Button } from "../../../../../empreenderUI/ui/Button";
import { ActionBar } from "../../../../../empreenderUI/ui/ActionBar";
import { useBlockNavigation } from "../../../../../empreenderUI/ui/NavigationBlocker";
import { translations } from "../../../../../utils/language";

const MAX_TEXT_LENGTH = 300;

const CopyField = ({ label, value, onCopy, multiline = false }) => {
	const language = useSelector((state) => state.reducerLanguage);
	return (
	<div className="flex flex-col gap-1.5">
		<div className="flex items-center justify-between gap-2">
			<label className="text-xs font-medium text-gray-700">{label}</label>
			<Button
				type="button"
				variant="ghost"
				size="icon"
				className="!size-7 shrink-0 text-gray-500 hover:text-gray-900"
				onClick={onCopy}
				title={translations["common-copy"][language.data]}
			>
				<Copy size={14} />
			</Button>
		</div>
		{multiline ? (
			<div className="rounded-md border border-gray-200 bg-gray-50 overflow-hidden">
				<pre className="max-h-36 overflow-auto px-3 py-2.5 font-mono text-[11px] leading-relaxed text-gray-600 whitespace-pre-wrap break-all custom-scroll">
					{value}
				</pre>
			</div>
		) : (
			<button
				type="button"
				onClick={onCopy}
				title={value}
				className="flex h-9 w-full items-center truncate rounded-md border border-gray-200 bg-gray-50 px-3 text-left font-mono text-[11px] text-gray-700 transition-colors hover:border-gray-300 hover:bg-gray-100"
			>
				{value}
			</button>
		)}
	</div>
	);
};

const PageCheckoutContent = () => {
	const { toggle } = useSubMenu();
	const history = useHistory();
	const dispatch = useDispatch();
	const language = useSelector((state) => state.reducerLanguage);
	const controlConfig = useSelector((state) => state.reducerControlConfig);
	const config = useSelector((state) => state.reducerTemplate);
	const saved = config?.data?.[0];

	const userToken = (() => {
		try {
			return JSON.parse(localStorage.getItem("depoimentos@login")).integration.user_token;
		} catch {
			return "";
		}
	})();

	const getCodePLataform = () => {
		try {
			const platform = JSON.parse(localStorage.getItem("depoimentos@login")).integration?.platform;
			switch (platform) {
				case "nuvemshop":
					return "1";
				case "shopify":
					return "4";
				case "yampi":
					return "2";
				default:
					return "1";
			}
		} catch {
			return "1";
		}
	};

	const link = `https://meuarquivodigital.com/suamidialink/${userToken}/${getCodePLataform()}`;

	const iframe = `<div style="display: flex; justify-content: center;">
  <iframe 
    src="${link}"
    style="
      border: none;
      width: 100%;
      max-width: 960px;
      height: 80vh;
    "
    loading="lazy">
  </iframe>
</div>`;

	const copyCode = () => {
		navigator.clipboard.writeText(link);
		toast.success(translations["thema-codigo-copiado"][language.data]);
	};

	const copyCodeIframe = () => {
		navigator.clipboard.writeText(iframe);
		toast.success(translations["thema-codigo-copiado"][language.data]);
	};

	const isDirty = useMemo(() => {
		if (!saved) return false;
		return (controlConfig.page_login_download_text ?? "") !== (saved.page_login_download_text ?? "");
	}, [controlConfig.page_login_download_text, saved]);

	useBlockNavigation(isDirty);

	const saveConfig = () => {
		dispatch({
			type: "SAGA_UPDATE_TEMPLATE",
			payload: {
				id: controlConfig?.id,
				page_login_download_text: controlConfig.page_login_download_text,
			},
		});
	};

	const discardChanges = () => {
		dispatch({ type: "SAGA_GET_TEMPLATES" });
	};

	const textLen = controlConfig.page_login_download_text?.length || 0;

	return (
		<ScrollArea>
			<Heading
				header={translations["thema-pagina-downloads"]?.[language.data] || "Página de downloads"}
				onClick={() => {
					history.push("/personalizar");
					toggle("personalizar");
				}}
			/>

			<div className="flex flex-col gap-5 px-6 py-4">
				<CopyField
					label={translations["thema-link"]?.[language.data] || "Link"}
					value={link}
					onCopy={copyCode}
				/>

				<CopyField
					label={translations["thema-iframe"]?.[language.data] || "iframe"}
					value={iframe}
					onCopy={copyCodeIframe}
					multiline
				/>

				<div className="flex flex-col gap-1.5">
					<div className="flex items-center justify-between gap-2">
						<label className="text-xs font-medium text-gray-700">
							{translations["thema-texto"]?.[language.data] || "Texto"}
						</label>
						<span className="text-[10px] tabular-nums text-gray-400">
							{MAX_TEXT_LENGTH - textLen}
						</span>
					</div>
					<Textarea
						value={controlConfig.page_login_download_text == null ? "" : controlConfig.page_login_download_text}
						minRows={5}
						maxRows={8}
						maxLength={MAX_TEXT_LENGTH}
						className="!border !border-solid !border-gray-200 !bg-white"
						onChange={(e) =>
							dispatch({
								type: "CONFIG_CONTROL_SET",
								payload: { page_login_download_text: e.target.value },
							})
						}
					/>
				</div>
			</div>

			<ActionBar
				open={isDirty}
				textos={{
					mainText: translations["thema-cuidado-salvar"]?.[language.data],
				}}
				onSave={(proceed) => {
					saveConfig();
					proceed();
				}}
				onCancel={discardChanges}
			/>
		</ScrollArea>
	);
};

export default PageCheckoutContent;
